<script setup lang="ts">
  import DocumentProcessingStatus from '@/components/app/cases/documents/DocumentProcessingStatus.vue'
  import Entities from '@/components/app/cases/entities/Entities.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import type { CustomerCase } from '@/types/generated'
  import { CheckCircle, Loader2 } from 'lucide-vue-next'
  import { computed } from 'vue'

  const { t } = useTranslation({
    en: {
      DocumentsBeingAnalyzedDataUpdate:
        'Documents are being analyzed, data will be updated soon. You can leave the tab and review the data later.',
      AllDocumentsAnalyzed: 'All documents have been analyzed'
    },
    de: {
      DocumentsBeingAnalyzedDataUpdate:
        'Dokumente werden analysiert, Daten werden bald aktualisiert. Sie können den Tab verlassen und die Daten später überprüfen.',
      AllDocumentsAnalyzed: 'Alle Dokumente wurden analysiert'
    }
  })
  const caseStore = useCaseStore()
  const caseRecord = computed<CustomerCase | null>(() => caseStore.caseRecord)

  // Get document processing data for DocumentProcessingStatus
  const documentProcessor = caseStore.documentProcessor
</script>

<template>
  <!-- Document Processing Status -->
  <DocumentProcessingStatus
    :use-slots="true"
    :documents="documentProcessor.documents"
    :processing-documents="documentProcessor.processingDocuments"
    :active-uploads="documentProcessor.activeUploads"
    :documents-with-errors="documentProcessor.documentsWithErrors"
  >
    <template #default="{ hasActivity, showSuccessMessage }">
      <div
        v-if="hasActivity || showSuccessMessage"
        class="mt-2 flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm"
        :class="[
          hasActivity
            ? 'border border-purple-200 bg-purple-50 text-purple-700'
            : 'border border-green-200 bg-green-50 text-green-700'
        ]"
      >
        <div class="flex flex-col items-center gap-1">
          <div class="flex items-center gap-2">
            <Loader2
              v-if="hasActivity"
              class="h-4 w-4 animate-spin text-purple-500"
            />
            <CheckCircle
              v-else
              class="h-4 w-4 text-green-500"
            />
            <span>
              {{ hasActivity ? t('DocumentsBeingAnalyzedDataUpdate') : t('AllDocumentsAnalyzed') }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </DocumentProcessingStatus>

  <div class="mt-10">
    <Entities v-if="caseRecord" />
  </div>
</template>
